import {useCallback, useEffect, useRef, useState} from 'react';
import DeliveryDataService, {FailedDeliveryOrder, uploadFailedPhotos} from "../../../../services/DeliveryDataService";
import {useDeliveryTaskOrderOperations} from '../../store/deliveryStore';

export interface UseFailedUploadRetryResult {
    failedUploadCount: number;
    failedUploads: FailedDeliveryOrder[];
    isRetrying: boolean;
    handleRetry: () => Promise<void>;
    showRetryingHint: boolean;
    isRetryReady: boolean;
}

export const useFailedUploadRetry = (): UseFailedUploadRetryResult => {
    const [failedUploadCount, setFailedUploadCount] = useState<number>(0);
    const [isRetrying, setIsRetrying] = useState<boolean>(false);
    const [failedUploads, setFailedUploads] = useState<FailedDeliveryOrder[]>([]);
    const {handleDelivered, handleDeliveryFailed} = useDeliveryTaskOrderOperations();
    const [showRetryingHint, setShowRetryingHint] = useState<boolean>(false);
    const hasInitialRetryRun = useRef<boolean>(false);
    const [isRetryReady, setIsRetryReady] = useState<boolean>(false);

    // 获取失败订单列表
    const getFailedOrders = useCallback(async () => {
        try {
            const orders = await DeliveryDataService.getAllDeliveryOrders();
            setFailedUploads(orders);
            return orders;
        } catch (error) {
            console.error('Failed to fetch orders:', error);
            return [];
        }
    }, []);

    // 获取失败订单数量
    const getFailedUploadsCount = useCallback(async () => {
        try {
            const count = await DeliveryDataService.getDeliveryOrderCount();
            setFailedUploadCount(count);
            return count;
        } catch (error) {
            console.error('Failed to fetch orders count:', error);
            return 0;
        }
    }, []);

    const handleRetry = useCallback(async () => {
        const currentCount = await getFailedUploadsCount();
        if (currentCount <= 0) {
            setIsRetrying(false);
            setShowRetryingHint(false);
            return;
        }

        const currentUploads = await getFailedOrders();
        if (currentUploads.length === 0) {
            setIsRetrying(false);
            setShowRetryingHint(false);
            return;
        }

        setIsRetrying(true);
        setShowRetryingHint(true);
        let allSuccess = true;

        try {
            for (const order of currentUploads) {
                const type = order.type;
                try {
                    // 过滤有效照片
                    const validPhotos = order.photos.filter(item => item?.photo?.objectKey && item?.photo?.url).map(item => item.photo);
                    
                    // 上传照片
                    const photoKeys = await uploadFailedPhotos(validPhotos, order.trackingNumber) || [];
                    
                    // ✅ 优先使用 completionLocation（完成时的GPS），否则使用 location
                    const completionLocation = order.completionLocation || order.location;
                    
                    if (type === 'delivered') {
                        await handleDelivered({
                            trackingNumber: order.trackingNumber,
                            location: {
                                latitude: completionLocation?.latitude || 0.0,
                                longitude: completionLocation?.longitude || 0.0
                            },
                            deliveredAt: order?.deliveredAt || new Date().toISOString(),
                            podImageS3KeysJson: photoKeys, // ✅ 保留旧字段
                            id: order.id,
                            ...(order.deliveryMethod ? { deliveryMethod: order.deliveryMethod } : {})
                        }, false);
                    } else {
                        await handleDeliveryFailed({
                            trackingNumber: order.trackingNumber,
                            location: {
                                latitude: completionLocation?.latitude || 0.0,
                                longitude: completionLocation?.longitude || 0.0
                            },
                            exceptionReasonCode: order.exceptionReasonCode ?? '',
                            id: order.id,
                            exceptionImageS3KeysJson: photoKeys, // ✅ 保留旧字段
                            ...(order.exceptionSubReasons != null && order.exceptionSubReasons.length > 0
                                ? { exceptionSubReasons: order.exceptionSubReasons }
                                : {})
                        }, false);
                    }

                    await DeliveryDataService.deleteDeliveryOrder(order.trackingNumber);
                } catch (error: any) {
                    console.log('Failed to retry upload:', error);
                    if (!error.networkError) {
                        await DeliveryDataService.deleteDeliveryOrder(order.trackingNumber);
                    }
                    allSuccess = false;
                    await new Promise(resolve => setTimeout(resolve, 1000));
                }
            }

            const remainingCount = await getFailedUploadsCount();

            if (allSuccess && remainingCount === 0) {
                setIsRetrying(false);
                setShowRetryingHint(false);
            } else if (remainingCount === 0) {
                setIsRetrying(false);
                setShowRetryingHint(false);
            }
        } catch (error) {
            console.log('Failed to retry upload:', error);
        } finally {
            setIsRetrying(false);
        }
    }, [getFailedUploadsCount, getFailedOrders, handleDelivered]);

    useEffect(() => {
        let intervalId: NodeJS.Timeout | null = null;

        const checkFailedUploads = async () => {
            const count = await getFailedUploadsCount();
            if (count > 0 && !isRetrying) {
                handleRetry();
            } else if (count === 0 && isRetrying) {
                setIsRetrying(false);
                setShowRetryingHint(false);
            }
        };

        if (!hasInitialRetryRun.current) {
            hasInitialRetryRun.current = true;
            checkFailedUploads();
        }

        intervalId = setInterval(async () => {
            const count = await getFailedUploadsCount();
            if (count > 0 && !isRetrying) {
                await handleRetry();
            }
        }, 60 * 1000);

        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [getFailedUploadsCount, handleRetry, isRetrying]);

    useEffect(() => {
        getFailedOrders();
        const initRetryState = async () => {
            await getFailedUploadsCount();
            setIsRetryReady(true);
        };
        initRetryState();
    }, [getFailedOrders, getFailedUploadsCount]);

    return {
        failedUploadCount,
        failedUploads,
        isRetrying,
        handleRetry,
        showRetryingHint,
        isRetryReady,
    };
};
